import type { RouteLocationNormalized } from 'vue-router';

/**
 * Important, main "offerflow" middleware applied to all steps of the offerflow.
 *
 * This is maybe the MOST IMPORTANT part of the application, so please read carefully:
 *
 * This middleware is re-run on every page navigation or reload within the offerFlow.
 * It makes sure you can never enter an offerflow step without having a product in the store.
 * It creates or copies the correct products and sets the correct passengers.
 *
 * 1. If there is an existing product in the PRODUCT store, proceed
 * 2. If there is no product in the PRODUCT store, but there is one in the CART store, copy it from there (this is the "edit product" case)
 * 3. If there is no product in either store, but the route is a booking flow, create a new product (this is the "new product" case)
 *     - For upsells, we require the product to already be setup.
 *
 * NEW PRODUCT: For a new product, it does the following:
 * 1. Find matching product config based on productKey in route
 * 2. Parse product config presets from URL (?cp=...) if available (for generic products!)
 * 3. Create a fresh product (product factory) and save it to the product store
 * 4. If there is a promotion code in the URL, apply it, which may set promotion related data on the product (e.g. for restricting calendar or trip selection)
 *
 * EDIT PRODUCT: For editing a product, it does the following:
 * 1. Copy the product from the cart store to the product store, so it can be edited without mutating the cart state until it's saved again
 * 2. Copy the passengers from the cart store as well, based on passengerRefs on the product
 */
export default defineNuxtRouteMiddleware(async (to) => {
    const { applyPageLayout, applySteps } = useFlow(to);
    const cartStore = useCartStore();
    const productStore = useProductStore();
    const productId = getProductIdFromRoute(to);
    const productKey = getProductKeyFromRoute(to);
    const flowType = getFlowTypeFromRoute(to);
    const { t: $t } = useNuxtApp().$i18n;

    const { applyListing } = useListings(to);
    const { applyContent } = useContent(to);

    // Abort if route is invalid (no productKey or no productId found)
    if (!productKey || !productId) {
        return abortNavigation();
    }

    const existingProduct = productStore.products.find(p => p.id === productId);

    // If the desired product is not yet in the product store, we either edit or create new:
    // If it exists, just go along
    if (!existingProduct) {
        // If the desired product is already in the cart, copy it from there (EDIT CASE)
        const cartProduct = cartStore.products.find(p => p.id === productId);
        if (cartProduct) {
            copyProductFromCart(productKey, productId, to);
        }
        // If not found in cart, but it's a booking flow, create a new product (NEW CASE)
        else if (flowType === 'booking') {
            await createFreshProduct(productKey, productId, to);
        }
        // For upsells, we require the product to already be setup, so if it's not in the product store or cart, we abort
        else {
            return abortNavigation();
        }
    }
    // If there is a mismatching between route flow type and existing product (e.g. existing product isUpsell but route is booking flow, or vice versa), abort
    else if ((existingProduct.isUpsell && flowType === 'booking') || (!existingProduct.isUpsell && flowType === 'upsell')) {
        return abortNavigation();
    }
    else if (existingProduct.config.isPromotionRequired && !existingProduct.promotion?.code) {
        throw new Error($t('offerFlow.promotion.validation.required'));
    }

    // Always (on every navigation, also for existing products), check if we need to re-fetch content or listing
    // Why not only initially? Because when changing locale, we need to re-fetch.
    await applyListing();
    await applyContent();

    // TODO
    applySteps();

    // Set the correct layout for the offer flow (e.g. subflow layout for upsells)
    applyPageLayout();

    // Strip pp param after applying
    if (to.query.pp) {
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { pp, ...cleanQuery } = to.query;
        return navigateTo({
            path: to.path,
            query: cleanQuery,
        }, { replace: true });
    }
});

/**
 *
 */
function copyProductFromCart(productKey: ProductKey, productId: ProductId, to: RouteLocationNormalized) {
    const cartStore = useCartStore();
    const productStore = useProductStore();
    const passengerStore = usePassengerStore();
    const cartProduct = cartStore.products.find(p => p.id === productId)!;
    // Find matching passenger
    const cartPassengers = cartStore.passengers.filter(p => cartProduct.passengerRefs.includes(p.ref));
    // If found in cart, "edit product", which means copy from cartstore
    productStore.editProduct(cartProduct);
    // If found in cart, copy passengers from cart as well
    passengerStore.setPassengers(cartPassengers);

    logSentryBreadcrumb(`Edit product from cart for id=${productId}, key=${productKey}`, { route: to.fullPath });
}

/**
 *
 */
async function createFreshProduct(productKey: ProductKey, productId: ProductId, to: RouteLocationNormalized) {
    const cartStore = useCartStore();
    const productStore = useProductStore();
    const sessionStore = useSessionStore();
    const { isFeatureEnabled } = useFeatureFlags();
    const { applyPromotionWithToasts } = usePromotions(to);
    const { parseProductConfigPreset } = useProductConfigPreset();
    const { parseProductPreset } = useProductPreset();

    // Parse product config presets from URL (?cp=...), e.g. preset origin/destination
    const productConfig = getProductConfigFromRoute(to);

    const productConfigPreset = isFeatureEnabled('product-config-presets') && productConfig?.isUsingProductConfigPresets
        ? await parseProductConfigPreset(productKey, to.query.cp ? decodeURIComponent(to.query.cp as string) : undefined)
        : undefined;

    // Parse product presets from URL (?pp=...), to set product instance state dynamically (prefill data)
    const productPreset = isFeatureEnabled('product-presets') && productConfig?.isUsingProductPresets
        ? await parseProductPreset(productKey, to.query.pp ? decodeURIComponent(to.query.pp as string) : undefined)
        : undefined;

    // Create a fresh product with optional presets
    productStore.initProduct(productKey, productId, productConfigPreset, productPreset);

    if (typeof to.query.backLink === 'string') {
        try {
            const url = new URL(to.query.backLink);
            if (url.protocol === 'https:' && (url.hostname === 'jungfrau.ch' || url.hostname.endsWith('.jungfrau.ch'))) {
                sessionStore.backLink = url.href;
            }
        }
        catch {
            // Ignore malformed back links.
        }
    }
    if (typeof to.query.affiliate === 'string' && to.query.affiliate) {
        sessionStore.affiliate = to.query.affiliate;
    }

    // Sets promotion related data, restricts calendar and trip selection etc.
    if (isFeatureEnabled('promotions')) {
        await applyPromotionWithToasts((to.query.promotion as string));
    }

    logSentryBreadcrumb(`Create fresh product for id=${productId}, key=${productKey}`, { route: to.fullPath, cartProducts: cartStore.products.map(p => p.id) });
}
